/**
 * 設定 → 報酬・通話料(2026-09-24 発注者指示「設定はメニューバーの設定から」「インセンティブは設定画面で設定できるように」)。
 *  - インセンティブ: 月間の取得件数の段階と金額(全アポインター)
 *  - 通話料の発信番号: アジャストワンCSVの「ご利用番号」→ 会社。同じ番号を別の会社へ引き継ぐときは行を足して適用開始日を入れる
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import NumInput from '@core/components/NumInput';
import { useToast } from '@core/components/Toast';
import { useUnsavedGuard } from '@core/components/UnsavedGuard';
import PageHeader from '@core/components/ui/PageHeader';
import Panel from '@core/components/ui/Panel';
import { formatCount, formatCurrency } from '@core/utils/formatters';
import type { Company, DateStr } from '@core/lib/types';
import { fetchCompanies } from './companyRepo';
import {
  DEFAULT_INCENTIVE,
  fetchCallNumbers,
  fetchIncentive,
  saveCallNumbers,
  saveIncentive,
  type CallNumber,
  type IncentiveSettings,
} from './settingsPayRepo';

export default function SettingsPayPage() {
  const toast = useToast();
  const { user } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [incentive, setIncentive] = useState<IncentiveSettings>(DEFAULT_INCENTIVE);
  const [savedIncentive, setSavedIncentive] = useState<IncentiveSettings>(DEFAULT_INCENTIVE);
  const [numbers, setNumbers] = useState<CallNumber[]>([]);
  const [savedNumbers, setSavedNumbers] = useState<CallNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cs, inc, cn] = await Promise.all([fetchCompanies(), fetchIncentive(), fetchCallNumbers()]);
      setCompanies(cs);
      setIncentive(inc);
      setSavedIncentive(inc);
      setNumbers(cn.items);
      setSavedNumbers(cn.items);
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const incentiveDirty = JSON.stringify(incentive) !== JSON.stringify(savedIncentive);
  const numbersDirty = JSON.stringify(numbers) !== JSON.stringify(savedNumbers);
  const guard = useUnsavedGuard(incentiveDirty || numbersDirty);
  const companyName = useMemo(() => new Map(companies.map((c) => [c.id, c.name])), [companies]);

  async function saveInc() {
    if (!user) return;
    for (const t of incentive.tiers) {
      if (!Number.isFinite(t.count) || t.count <= 0 || !Number.isFinite(t.amount) || t.amount < 0) {
        toast('件数は1以上、金額は0以上の数字で入れてください');
        return;
      }
    }
    setBusy(true);
    try {
      const next = { ...incentive, tiers: [...incentive.tiers].sort((a, b) => a.count - b.count) };
      await saveIncentive(next, user.id);
      setIncentive(next);
      setSavedIncentive(next);
      toast('インセンティブの設定を保存しました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function saveNums() {
    if (!user) return;
    for (const n of numbers) {
      if (n.number.trim() === '') {
        toast('番号を入れてください(空の行は削除してください)');
        return;
      }
      if (n.from !== '' && !/^\d{4}-\d{2}-\d{2}$/.test(n.from)) {
        toast('適用開始日は 2026-10-01 の形で入れてください');
        return;
      }
    }
    setBusy(true);
    try {
      await saveCallNumbers({ items: numbers }, user.id);
      setSavedNumbers(numbers);
      toast('発信番号の設定を保存しました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const setTier = (i: number, patch: Partial<{ count: number; amount: number }>) =>
    setIncentive((prev) => ({ ...prev, tiers: prev.tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)) }));
  const setNum = (i: number, patch: Partial<CallNumber>) => setNumbers((prev) => prev.map((n, j) => (j === i ? { ...n, ...patch } : n)));

  return (
    <div className="screen">
      {guard.modal}
      <PageHeader title="報酬・通話料の設定" description="インセンティブの段階と、通話料CSVの発信番号と会社の対応を決めます" />

      <Panel
        id="incentive"
        title="インセンティブ"
        meta={incentiveDirty ? '未保存' : undefined}
        note="**月の取得件数**が段階の件数**以上**になったら、その段階の金額を支給します(一番上の段階だけ。重ねて足しません)。対象は**全アポインター**です"
      >
        {loading ? (
          <div className="ui-empty">読み込み中…</div>
        ) : (
          <>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 'var(--space-3)' }}>
              <input type="checkbox" checked={incentive.enabled} onChange={(e) => setIncentive((p) => ({ ...p, enabled: e.target.checked }))} />
              インセンティブを使う
            </label>
            <div className="table-scroll">
              <table className="fit-table no-stack">
                <thead>
                  <tr>
                    <th>月の取得件数(以上)</th>
                    <th>支給額(円)</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {incentive.tiers.map((t, i) => (
                    <tr key={i}>
                      <td>
                        <NumInput value={String(t.count)} onChange={(v) => setTier(i, { count: Number(v || 0) })} />
                      </td>
                      <td>
                        <NumInput value={String(t.amount)} onChange={(v) => setTier(i, { amount: Number(v || 0) })} />
                      </td>
                      <td>
                        <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => setIncentive((p) => ({ ...p, tiers: p.tiers.filter((_, j) => j !== i) }))}>
                          削除
                        </button>
                      </td>
                    </tr>
                  ))}
                  {incentive.tiers.length === 0 && (
                    <tr>
                      <td colSpan={3} className="cell-na">
                        段階がありません
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="ui-hint" style={{ marginTop: 'var(--space-2)' }}>
              例: {incentive.tiers.map((t) => `${formatCount(t.count, { unit: '件' })}=${formatCurrency(t.amount)}`).join(' ／ ')}
            </p>
            <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
              <button type="button" className="ui-btn ui-btn-primary" disabled={busy || !incentiveDirty} onClick={() => void saveInc()}>
                保存する
              </button>
              <button type="button" className="ui-btn ui-btn-secondary" disabled={busy} onClick={() => setIncentive((p) => ({ ...p, tiers: [...p.tiers, { count: 0, amount: 0 }] }))}>
                ＋ 段階を追加
              </button>
              <button type="button" className="ui-btn ui-btn-secondary" disabled={busy || !incentiveDirty} onClick={() => setIncentive(savedIncentive)}>
                元に戻す
              </button>
            </div>
          </>
        )}
      </Panel>

      <Panel
        id="call-numbers"
        title="通話料の発信番号"
        meta={numbersDirty ? '未保存' : undefined}
        note="アジャストワンのCSVの「**ご利用番号**」がどの会社かを決めます。**同じ番号を別の会社へ引き継ぐとき**は行を足して、**適用開始日**(例 2026-10-01)を入れてください。その日以降は新しい会社に付きます。会社を空にすると**対象外**(社外・自社の番号)として通話料に入れません"
      >
        {loading ? (
          <div className="ui-empty">読み込み中…</div>
        ) : (
          <>
            <div className="table-scroll">
              <table className="fit-table no-stack">
                <thead>
                  <tr>
                    <th style={{ width: 150 }}>ご利用番号</th>
                    <th style={{ width: 220 }}>会社</th>
                    <th style={{ width: 150 }}>適用開始(空=最初から)</th>
                    <th>メモ・会社マスタに無い名前</th>
                    <th style={{ width: 70 }} />
                  </tr>
                </thead>
                <tbody>
                  {numbers.map((n, i) => (
                    <tr key={i}>
                      <td>
                        <input value={n.number} onChange={(e) => setNum(i, { number: e.target.value })} style={{ width: '100%' }} />
                      </td>
                      <td>
                        <select value={n.company_id} onChange={(e) => setNum(i, { company_id: e.target.value })} style={{ width: '100%' }}>
                          <option value="">(対象外・未登録)</option>
                          {companies.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input value={n.from} placeholder="2026-10-01" onChange={(e) => setNum(i, { from: e.target.value as DateStr | '' })} style={{ width: '100%' }} />
                      </td>
                      <td>
                        <input value={n.label || n.note} onChange={(e) => setNum(i, { label: e.target.value })} style={{ width: '100%' }} placeholder="ファミー湯 など" />
                      </td>
                      <td>
                        <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => setNumbers((p) => p.filter((_, j) => j !== i))}>
                          削除
                        </button>
                      </td>
                    </tr>
                  ))}
                  {numbers.length === 0 && (
                    <tr>
                      <td colSpan={5} className="cell-na">
                        まだ登録がありません
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="ui-hint" style={{ marginTop: 'var(--space-2)' }}>
              登録 {formatCount(numbers.length, { unit: '件' })} ／ 会社に紐付いている {formatCount(numbers.filter((n) => n.company_id).length, { unit: '件' })}
              {numbers.some((n) => n.company_id && !companyName.has(n.company_id)) ? ' ／ ※会社マスタに無いIDがあります' : ''}
            </p>
            <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
              <button type="button" className="ui-btn ui-btn-primary" disabled={busy || !numbersDirty} onClick={() => void saveNums()}>
                保存する
              </button>
              <button type="button" className="ui-btn ui-btn-secondary" disabled={busy} onClick={() => setNumbers((p) => [...p, { number: '', company_id: '', label: '', from: '', note: '' }])}>
                ＋ 番号を追加
              </button>
              <button type="button" className="ui-btn ui-btn-secondary" disabled={busy || !numbersDirty} onClick={() => setNumbers(savedNumbers)}>
                元に戻す
              </button>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}
